import React from 'react';
import { API } from '../api';
import { Field, Section, Segmented, Toggle } from './ui';
import AdminPlans from './AdminPlans';
import { downloadFile } from '../utils/download';

/*
 * What each club is on, what it is using, and what it has been given.
 *
 * This replaced a row of unlabelled number boxes. The numbers were right and
 * meant nothing: "Per month", "Keeps", "Photos/issue" and a bare "Stored"
 * told you neither what a club was paying for nor how close it was to the
 * edge of it.
 *
 * The three limits are limited for two different reasons, and the page says
 * so. Generations cost money on a shared API key and are metered monthly.
 * Posters and newsletter photographs cost room in the database and are kept
 * for good, so they are a standing total — a club does not use up ten posters
 * in March, it keeps them.
 *
 * Nothing here takes payment. A top-up is a site admin recording what a club
 * was given and why, and the ledger is the memory of that.
 */

const pct = (used, cap) => (cap > 0 ? Math.min(100, Math.round((used / cap) * 100)) : 0);
const capText = cap => (cap === null || cap === undefined ? '∞' : cap);

/** One limit, as a bar you can read at a glance. */
function Meter({ label, used, cap, extra, note, unit }) {
  const unlimited = cap === null || cap === undefined;
  const filled = unlimited ? 0 : pct(used, cap);
  const full = !unlimited && used >= cap;
  return (
    <div className="sub-meter">
      <div className="sub-meter-head">
        <span className="sub-meter-label">{label}</span>
        <span className={`sub-meter-figure${full ? ' is-full' : ''}`}>
          {used}<span className="sub-meter-of"> / {capText(cap)}</span>
          {extra > 0 && <span className="sub-meter-extra"> +{extra}</span>}
        </span>
      </div>
      <div className="sub-bar" role="img"
        aria-label={`${used} of ${capText(cap)} ${unit || ''} used`.trim()}>
        <span className={`sub-bar-fill${full ? ' is-full' : ''}`} style={{ width: filled + '%' }} />
      </div>
      {note && <p className="sub-meter-note">{note}</p>}
    </div>
  );
}

/** A limit an admin can override for one club, or hand back to the plan. */
function LimitBox({ label, value, planValue, onSet, help }) {
  const overridden = value !== null && value !== undefined;
  return (
    <Field label={label} help={help}>
      <div className="sub-limit">
        <input className="w3-input" type="number" min="0"
          aria-label={label}
          placeholder={String(planValue)}
          value={overridden ? value : ''}
          onChange={e => onSet(e.target.value === '' ? null : e.target.value)} />
        {overridden
          ? <button type="button" className="sub-clear" onClick={() => onSet(null)}>
            use plan ({planValue})
          </button>
          : <span className="sub-from-plan">from plan</span>}
      </div>
    </Field>
  );
}

function ClubCard({ club, plans, onPatch, onCredit, showToast }) {
  const [open, setOpen] = React.useState(false);
  const [ledger, setLedger] = React.useState(null);
  const [amount, setAmount] = React.useState('');
  const [note, setNote] = React.useState('');
  const [busy, setBusy] = React.useState(false);

  const plan = plans.find(p => p.slug === club.plan) || plans[0] || {};

  const loadLedger = React.useCallback(async () => {
    const r = await fetch(`${API}/api/subscriptions/${club.id}/ledger`);
    if (r.ok) setLedger(await r.json());
  }, [club.id]);

  React.useEffect(() => { if (open) loadLedger(); }, [open, loadLedger]);

  const addCredits = async () => {
    const n = parseInt(amount, 10);
    if (Number.isNaN(n) || n === 0) return showToast('Say how many generations to add', 'orange');
    setBusy(true);
    try {
      await onCredit(club, n, note);
      setAmount(''); setNote('');
      await loadLedger();
    } finally { setBusy(false); }
    return undefined;
  };

  return (
    <div className="sub-card">
      <div className="sub-card-head">
        <div>
          <h3 className="sub-club">{club.name}</h3>
          <span className="sub-slug">/{club.slug}</span>
        </div>
        <div className="sub-plan-pick">
          <Segmented
            ariaLabel={`Plan for ${club.name}`}
            name={`plan-${club.id}`}
            value={club.plan}
            onChange={v => onPatch(club, { plan: v })}
            options={plans.map(p => ({ value: p.slug, label: p.label }))}
          />
          <span className="sub-price">{plan ? plan.price : ''}<span className="sub-per"> /month</span></span>
        </div>
      </div>

      {plan && <p className="sub-blurb">{plan.blurb}</p>}

      <div className="sub-listed">
        <Toggle label="Listed publicly" checked={!!club.listedPublicly}
          onChange={v => onPatch(club, { listedPublicly: v })} />
        <p className="sub-meter-note">
          {club.listedPublicly
            ? 'Offered on the login page for registering, joining and club news.'
            : 'Hidden from the login page. Members and admins still use it as normal, and direct links still work. For the Default Club and demo clubs.'}
        </p>
      </div>

      <div className="sub-meters">
        <Meter label="Generations this month" unit="generations"
          used={club.generations.used} cap={club.generations.cap}
          extra={club.generations.credits}
          note={club.generations.credits
            ? `${club.generations.credits} bought credits, used only once the month's allowance is gone`
            : 'Images made by the model. Resets on the first of the month.'} />
        <Meter label="Posters kept" unit="posters"
          used={club.posters.stored} cap={club.posters.cap}
          note="Not a monthly figure — posters are kept until somebody deletes them." />
        <Meter label="Photos kept" unit="photos"
          used={club.photos.stored} cap={club.photos.cap}
          note="Uploaded photographs, across the whole club. Counted apart from posters." />
        <Meter label="Members" unit="members"
          used={club.members.count} cap={club.members.cap}
          note="Guests entered for a competition are not members and are not counted." />
        <Meter label="Admin accounts" unit="admins"
          used={club.admins.count} cap={club.admins.cap}
          note="Club admins and site admins belonging to this club." />
      </div>

      <button className="w3-button w3-small w3-border sub-more" onClick={() => setOpen(!open)}>
        <i className={`fa fa-${open ? 'chevron-up' : 'chevron-down'} w3-margin-right`}></i>
        {open ? 'Done' : 'Adjust limits and credits'}
      </button>

      {open && (
        <div className="sub-detail">
          <div className="gs-field-grid" style={{ '--gs-cols': 'repeat(auto-fit, minmax(190px, 1fr))' }}>
            <LimitBox label="Generations a month"
              value={club.overrides.posterMonthlyCap}
              planValue={plan.generationsPerMonth}
              help="Leave blank to follow the plan."
              onSet={v => onPatch(club, { posterMonthlyCap: v })} />
            <LimitBox label="Posters kept"
              value={club.overrides.posterStorageCap}
              planValue={plan.postersKept}
              help="Leave blank to follow the plan."
              onSet={v => onPatch(club, { posterStorageCap: v })} />
            <LimitBox label="Photos kept"
              value={club.overrides.newsletterPhotoCap}
              planValue={plan.photosKept}
              help="Uploads, across the whole club."
              onSet={v => onPatch(club, { newsletterPhotoCap: v })} />
            <LimitBox label="Members"
              value={club.overrides.maxMembersOverride}
              planValue={plan.maxMembers}
              help="Leave blank to follow the plan."
              onSet={v => onPatch(club, { maxMembersOverride: v })} />
            <LimitBox label="Admin accounts"
              value={club.overrides.maxAdminsOverride}
              planValue={plan.maxAdmins}
              help="Leave blank to follow the plan."
              onSet={v => onPatch(club, { maxAdminsOverride: v })} />
          </div>

          <div className="sub-credits">
            <Field label="Add generation credits"
              help="Recorded, not charged — handle the money however you do now. Credits never expire and a negative number takes some back.">
              <div className="sub-credit-row">
                <input className="w3-input" type="number" placeholder="25"
                  aria-label={`Credits to add for ${club.name}`}
                  value={amount} onChange={e => setAmount(e.target.value)} />
                <input className="w3-input" placeholder="paid by invoice 1042"
                  aria-label={`Note for ${club.name}`}
                  value={note} onChange={e => setNote(e.target.value)} />
                <button className="w3-button gs-btn-primary" onClick={addCredits} disabled={busy}>
                  Add credits
                </button>
              </div>
            </Field>
          </div>

          {ledger && ledger.length > 0 && (
            <div className="sub-ledger">
              <span className="gs-section-label">Account</span>
              <ul>
                {ledger.map(l => (
                  <li key={l.id}>
                    <span className={`sub-amount${l.amount < 0 ? ' is-negative' : ''}`}>
                      {l.amount > 0 ? '+' : ''}{l.amount}
                    </span>
                    <span className="sub-ledger-note">{l.note || 'no note'}</span>
                    <span className="sub-ledger-meta">
                      balance {l.balanceAfter}
                      {l.grantedBy ? ' · ' + l.grantedBy : ''}
                      {' · '}{new Date(l.createdAt).toLocaleDateString('en-NZ',
                        { day: 'numeric', month: 'short', year: 'numeric' })}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* Everything about one club, as a zip — for a club leaving, asking for its
   records, or a dispute. Every member's personal details are in it, so the
   button says so before it is pressed. See server/utils/clubExport.js. */
function ClubDownload({ club, showToast }) {
  const [busy, setBusy] = React.useState(false);
  const go = async () => {
    setBusy(true);
    try {
      await downloadFile(`${API}/api/auth/clubs/${club.id}/export`, 'greensheet-club.zip');
    } catch (err) {
      showToast(err.message, 'red');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="sub-download">
      <button type="button" className="w3-button w3-round w3-border" onClick={go} disabled={busy}
        aria-label={`Download everything about ${club.name}`}>
        <i className={`fa ${busy ? 'fa-spinner fa-spin' : 'fa-download'} w3-margin-right`} aria-hidden="true"></i>
        {busy ? 'Preparing…' : 'Download everything'}
      </button>
      <span>Members, competitions, results, newsletters and pictures, as a zip. It contains members’ personal details — keep it secure.</span>
    </div>
  );
}

function AdminSubscriptions({ clubs, createClub, deleteClub, newClubName, setNewClubName, showToast }) {
  const [subs, setSubs] = React.useState(null);
  const [plans, setPlans] = React.useState([]);
  const [tab, setTab] = React.useState('clubs');
  const [error, setError] = React.useState('');

  const load = React.useCallback(async () => {
    const [s, p] = await Promise.all([
      fetch(`${API}/api/subscriptions`),
      fetch(`${API}/api/subscriptions/plans`),
    ]);
    if (p.ok) setPlans(await p.json());
    if (s.ok) setSubs(await s.json());
    else setError('Could not load the clubs.');
  }, []);

  React.useEffect(() => { load(); }, [load, clubs.length]);

  const replace = updated => setSubs(list => list.map(c => (c.id === updated.id ? updated : c)));

  const onPatch = async (club, patch) => {
    const r = await fetch(`${API}/api/subscriptions/${club.id}`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    });
    if (!r.ok) return showToast('That change did not save', 'red');
    return replace(await r.json());
  };

  const onCredit = async (club, amount, note) => {
    const r = await fetch(`${API}/api/subscriptions/${club.id}/credits`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ amount, note }),
    });
    const data = await r.json().catch(() => ({}));
    if (!r.ok) return showToast(data.error || 'Those credits did not save', 'red');
    replace(data);
    return showToast(amount > 0 ? `${amount} credits added` : `${-amount} credits taken back`, 'green');
  };

  return (
    <div className="gs-panel">
      <div className="gs-tabs w3-margin-bottom">
        <button className={`gs-tab ${tab === 'clubs' ? 'gs-tab-on' : ''}`} onClick={() => setTab('clubs')}>
          Clubs
        </button>
        <button className={`gs-tab ${tab === 'plans' ? 'gs-tab-on' : ''}`} onClick={() => setTab('plans')}>
          Plans
        </button>
      </div>

      {tab === 'plans' && <AdminPlans showToast={showToast} onPlansChanged={load} />}

      {tab === 'clubs' && (
      <Section label={`Clubs (${clubs.length})`}>
        {error && <div className="w3-panel w3-pale-red w3-leftbar w3-border-red"><p>{error}</p></div>}

        <div style={{ display: 'flex', gap: 'var(--gs-space-2)', maxWidth: 'var(--gs-content-narrow)' }}>
          <input className="w3-input" style={{ flex: 1, minWidth: 0 }}
            placeholder="New club name" aria-label="New club name"
            value={newClubName}
            onChange={e => setNewClubName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && createClub()} />
          <button className="w3-button w3-round"
            style={{ minHeight: 'var(--gs-control-md)', padding: '0 22px', background: 'var(--gs-accent-fill, var(--club-30))', color: '#fff', fontWeight: 600 }}
            onClick={createClub} disabled={!newClubName.trim()}>
            Add
          </button>
        </div>

        {!subs ? (
          <p className="w3-text-grey" style={{ marginTop: 'var(--gs-space-6)' }}>Loading…</p>
        ) : (
          <div className="sub-list">
            {subs.map(c => (
              <div key={c.id} className="sub-wrap">
                <ClubCard club={c} plans={plans} onPatch={onPatch} onCredit={onCredit}
                  showToast={showToast} />
                <ClubDownload club={c} showToast={showToast} />
                <button className="w3-button w3-round sub-delete"
                  onClick={() => deleteClub(c.id, c.name)}
                  title={`Delete ${c.name}`} aria-label={`Delete ${c.name}`}>
                  <i className="fa fa-trash"></i>
                </button>
              </div>
            ))}
          </div>
        )}
      </Section>
      )}
    </div>
  );
}

export default AdminSubscriptions;
